import { addDays, subDays } from 'date-fns'
import { when } from 'mobx'

import { isInView } from '@avantstay/backoffice-core'

import { extendedColumnsStore } from '@new/domains/fastBookingsTapeChart/components/ExtendedColumns/ExtendedColumnsStore'
import {
  PREV_NEXT_BUTTONS_WIDTH,
  TAPECHART_HEADER_ROW_HEIGHT,
  TIME_FRAME_DAYS,
} from '@new/domains/fastBookingsTapeChart/config/contants'
import {
  fastBookingsTapeChartFiltersStore,
  fastBookingsTapeChartStore,
  tapechartGridSizingStore,
} from '@new/domains/fastBookingsTapeChart/internal'

enum ScrollDirection {
  Previous = 'previous',
  Next = 'next',
}

class ScrollController {
  private _container: HTMLDivElement | undefined = undefined

  private timeoutId: any

  private desiredScrollLeftPosition = 0

  setContainer(container: HTMLDivElement | null) {
    if (container && this._container !== container) {
      this._container = container
    }
  }

  addScrollListener(callback: VoidFunction) {
    if (this._container) {
      this._container.addEventListener('scroll', callback)
    }
  }

  removeScrollListener(callback: VoidFunction) {
    if (this._container) {
      this._container.removeEventListener('scroll', callback)
    }
  }

  get container() {
    return this._container
  }

  scrollTo(options: ScrollToOptions) {
    if (!this._container) {
      return
    }

    this._container.scrollTo(options)
  }

  scrollBy(options: ScrollToOptions) {
    if (!this._container) {
      return
    }
    this._container.scrollBy(options)
  }

  private isSwitchingViewFromNeeded = (direction: ScrollDirection) => {
    const offset = tapechartGridSizingStore.dayColumnWidth * 7
    if (!this._container) {
      return undefined
    }
    if (direction === ScrollDirection.Previous) {
      return this._container.scrollLeft <= offset
    }
    return (
      this._container.scrollWidth - this._container.scrollLeft - this._container.clientWidth <=
      offset
    )
  }

  private getWeekScrollValue(direction: ScrollDirection) {
    if (!this._container) {
      return 0
    }

    const { extendedColumnsWidth } = extendedColumnsStore

    const scrollRest = this._container.scrollLeft % tapechartGridSizingStore.dayColumnWidth

    if (direction === ScrollDirection.Previous) {
      return -tapechartGridSizingStore.dayColumnWidth * 7 - scrollRest
    }

    const containerRest =
      (this._container.clientWidth -
        tapechartGridSizingStore.firstColumnWidth -
        extendedColumnsWidth) %
      tapechartGridSizingStore.dayColumnWidth

    return (
      tapechartGridSizingStore.dayColumnWidth * 7 -
      containerRest -
      scrollRest +
      PREV_NEXT_BUTTONS_WIDTH
    )
  }

  private loadPreviousRange() {
    const { setViewFrom, startDate } = fastBookingsTapeChartFiltersStore
    fastBookingsTapeChartStore.forceLoadingOverlay = true

    setViewFrom(subDays(startDate, TIME_FRAME_DAYS / 2))

    when(
      () => !fastBookingsTapeChartStore.isLoading,
      () => {
        this.scrollTo({
          left: (this.getDistance()?.left ?? 0) + this.getWeekScrollValue(ScrollDirection.Previous),
          behavior: 'smooth',
        })
      },
    )
  }

  private loadNextRange() {
    const { setViewFrom, startDate } = fastBookingsTapeChartFiltersStore
    fastBookingsTapeChartStore.forceLoadingOverlay = true

    setViewFrom(addDays(startDate, TIME_FRAME_DAYS / 2))

    when(
      () => !fastBookingsTapeChartStore.isLoading,
      () => {
        this.scrollTo({
          left: (this.getDistance()?.left ?? 0) + this.getWeekScrollValue(ScrollDirection.Next),
          behavior: 'smooth',
        })
      },
    )
  }

  scrollToPreviousWeek() {
    if (!this._container) {
      return
    }

    if (this.isSwitchingViewFromNeeded(ScrollDirection.Previous)) {
      this.desiredScrollLeftPosition =
        (TIME_FRAME_DAYS / 2) * tapechartGridSizingStore.dayColumnWidth +
        (this.getDistance()?.left || 0)

      this.loadPreviousRange()
    } else {
      this.scrollBy({
        left: this.getWeekScrollValue(ScrollDirection.Previous),
        behavior: 'smooth',
      })
    }
  }

  scrollToNextWeek() {
    if (!this._container) {
      return
    }

    if (this.isSwitchingViewFromNeeded(ScrollDirection.Next)) {
      this.desiredScrollLeftPosition =
        this._container.scrollLeft - (TIME_FRAME_DAYS / 2) * tapechartGridSizingStore.dayColumnWidth

      this.loadNextRange()
    } else {
      this._container.scrollBy({
        left: this.getWeekScrollValue(ScrollDirection.Next),
        behavior: 'smooth',
      })
    }
  }

  scrollToDesiredLeftPosition() {
    this.scrollTo({ left: this.desiredScrollLeftPosition })

    if (this.desiredScrollLeftPosition !== 0) {
      fastBookingsTapeChartStore.forceLoadingOverlay = false

      this.desiredScrollLeftPosition = 0
    }
  }

  scrollIntoView(target?: EventTarget | HTMLDivElement | null) {
    if (target instanceof HTMLElement) {
      clearTimeout(this.timeoutId)
      this.timeoutId = setTimeout(
        () =>
          !isInView(target, true, this._container) &&
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          }),
        1000,
      )
    }
  }

  getContainerAsync(): Promise<HTMLDivElement> {
    return new Promise(resolve => {
      const checkForContainer = () => {
        if (this._container) {
          resolve(this._container)
        } else {
          setTimeout(checkForContainer, 200)
        }
      }

      checkForContainer()
    })
  }

  async scrollToRow(rowId: number) {
    const container = await this.getContainerAsync()
    container.scrollTo({
      top:
        rowId * tapechartGridSizingStore.propertyRowHeight -
        container.clientHeight / 2 +
        TAPECHART_HEADER_ROW_HEIGHT * 2,
      behavior: 'smooth',
    })
  }

  getDistance() {
    if (this._container) {
      return {
        left: this._container.scrollLeft,
        right:
          this._container.scrollWidth - this._container.scrollLeft - this._container.clientWidth,
      }
    }
    return undefined
  }
}

export const scrollController = new ScrollController()
